import React, { useEffect, useState } from 'react';
import type { ResolvedPhoneShell } from 'magical-trails';

import Avatar from './Avatar';
import { clockFace } from './clock';
import PhoneGlyph, { type GlyphId } from './PhoneGlyph';

export interface HomeApp {
  id: GlyphId;
  label: string;
  badge?: number;
  onOpen: () => void;
}

export interface LatestMessage {
  name: string;
  avatar?: string;
  group: boolean;
  line: string;
  unread: number;
  onOpen: () => void;
}

export interface Props {
  shell: ResolvedPhoneShell;
  storyTitle: string;
  apps: HomeApp[];
  /** The most recent conversation, if there is one yet. */
  latest?: LatestMessage;
}

function today(): { time: string; date: string } {
  const now = new Date();
  return {
    time: clockFace(now),
    date: now.toLocaleDateString([], {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }),
  };
}

/** The home screen: a big clock, the story's name, and the apps. */
const PhoneHome: React.FC<Props> = ({ shell, storyTitle, apps, latest }) => {
  const [clock, setClock] = useState(today);
  useEffect(() => {
    const timer = setInterval(() => setClock(today()), 15_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="Phone_screen Phone_home" aria-label="Home screen">
      <div className="Phone_wallpaper" aria-hidden="true" />
      <div className="Phone_homeHeader">
        <p className="Phone_homeClock">{clock.time}</p>
        <p className="Phone_homeDate">{clock.date}</p>
        <h1 className="Phone_homeTitle">{storyTitle}</h1>
      </div>
      {latest && (
        <button type="button" className="Phone_latest" onClick={latest.onOpen}>
          <Avatar
            name={latest.name}
            src={latest.avatar}
            group={latest.group}
            size="sm"
          />
          <span className="Phone_latestText">
            <strong>{latest.name}</strong>
            <span>{latest.line}</span>
          </span>
          {latest.unread > 0 && (
            <span className="Phone_badge">
              {latest.unread}
              <span className="sr-only"> unread</span>
            </span>
          )}
        </button>
      )}
      <ul className="Phone_appGrid">
        {apps.map((app) => (
          <li key={app.id}>
            <button
              type="button"
              className="Phone_app"
              onClick={app.onOpen}
              // Label first, then the count: "Messages, 2 unread".
              aria-label={
                app.badge ? `${app.label}, ${app.badge} unread` : app.label
              }
            >
              <span className={`Phone_appTile Phone_appTile-${app.id}`}>
                <PhoneGlyph glyph={app.id} theme={shell.theme} />
                {app.badge ? (
                  <span className="Phone_badge Phone_badge-corner">
                    {app.badge}
                    <span className="sr-only"> unread</span>
                  </span>
                ) : null}
              </span>
              <span className="Phone_appLabel">{app.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default PhoneHome;
